import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../../api/api";
import { getFilenameFromContentDisposition } from "../../../helpers";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { ICustomerStatement } from "./CustomerStatementReport";

function generateAdvanceFilterQuery(data: any) {
  const filters = [];

  for (const [key, value] of Object.entries(data)) {
    if (value !== "" && value !== null) {
      let filterObj = {
        column: [key],
        operator: "EQUAL",
        value: value,
        logicalOperator: "AND",
      };
      if (key === "transaction_date") {
        filterObj = {
          column: [key],
          operator: "CONTAINS",
          value: value + "-",
          logicalOperator: "AND",
        };
      }
      if (key === "user_fact_id") {
        const filterObj = {
          column: ["producer_one_id", "producer_two_id", "producer_three_id"],
          operator: "EQUAL",
          value: value,
          logicalOperator: "AND",
        };

        const encodedFilter = encodeURIComponent(
          JSON.stringify(filterObj, null, 2),
        );
        filters.push(`advanceFilter=${encodedFilter}`);
      } else {
        const encodedFilter = encodeURIComponent(
          JSON.stringify(filterObj, null, 2),
        );
        filters.push(`advanceFilter=${encodedFilter}`);
      }
    }
  }

  return filters.join("&");
}

export const FETCH_CUSTOMER_STATEMENT_LIST_PROGRESS =
  "FETCH_CUSTOMER_STATEMENT_LIST_PROGRESS";
export const FETCH_CUSTOMER_STATEMENT_LIST_SUCCESS =
  "FETCH_CUSTOMER_STATEMENT_LIST_SUCCESS";
export const FETCH_CUSTOMER_STATEMENT_LIST_FAILURE =
  "FETCH_CUSTOMER_STATEMENT_LIST_FAILURE";

export const fetchCustomerStatementListProgress = () =>
  action(FETCH_CUSTOMER_STATEMENT_LIST_PROGRESS);
export const fetchCustomerStatementListSuccess = (list: ICustomerStatement[]) =>
  action(FETCH_CUSTOMER_STATEMENT_LIST_SUCCESS, { list });
export const fetchCustomerStatementListFailure = () =>
  action(FETCH_CUSTOMER_STATEMENT_LIST_FAILURE);

export const fetchCustomerStatementListAsync =
  (
    searchState: { [key: string]: any },
    customer_id?: number,
    customer_policy_id?: number,
    user_code?: string,
    callProducerStatementApi?: boolean,
    callBranchStatementApi?: boolean,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchCustomerStatementListProgress());
      if (customer_id) {
        searchState["customer_id"] = customer_id;
      }
      if (customer_policy_id) {
        searchState["customer_policy_id"] = customer_policy_id;
      }
      if (user_code) {
        searchState["user_code"] = user_code;
      }

      const {
        invoice_payment_to_date,
        invoice_payment_from_date,
        ...restSearchState
      } = searchState;

      const query = generateAdvanceFilterQuery(restSearchState);
      let finalUrl = `/accounting/get-customer-statement?`;
      if (callProducerStatementApi) {
        finalUrl = `/accounting/get-producer-statement?`;
      }
      if(callBranchStatementApi){
        finalUrl = `/accounting/get-branch-statement?`;
      }
      if (invoice_payment_from_date) {
        const filterObj = {
          column: ["invoice_or_payment_date"],
          operator: "GREATER_THAN_EQUAL",
          value: invoice_payment_from_date,
          logicalOperator: "AND",
        };

        const encodedFilter = encodeURIComponent(
          JSON.stringify(filterObj, null, 2),
        );
        finalUrl += `advanceFilter=${encodedFilter}&`;
      }
      if (invoice_payment_to_date) {
        const filterObj = {
          column: ["invoice_or_payment_date"],
          operator: "LESSER_THAN_EQUAL",
          value: invoice_payment_to_date,
          logicalOperator: "AND",
        };

        const encodedFilter = encodeURIComponent(
          JSON.stringify(filterObj, null, 2),
        );
        finalUrl += `advanceFilter=${encodedFilter}&`;
      }

      if (query) {
        finalUrl += query;
      }

      const res = await api.get(finalUrl);
      const data = res.data.data;
      dispatch(fetchCustomerStatementListSuccess(data));
    } catch (err: any) {
      dispatch(fetchCustomerStatementListFailure());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const fetchCustomerStatementForExportAsync =
  (
    searchState: { [key: string]: any },
    onCallback: (data: ICustomerStatement[]) => void,
    customer_id?: number,
    customer_policy_id?: number,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      if (customer_id) {
        searchState["customer_id"] = customer_id;
      }
      if (customer_policy_id) {
        searchState["customer_policy_id"] = customer_policy_id;
      }

      const {
        invoice_payment_to_date,
        invoice_payment_from_date,
        ...restSearchState
      } = searchState;

      const query = generateAdvanceFilterQuery(restSearchState);
      let finalUrl = `/accounting/get-customer-statement?is_downloading=true`;
      if (invoice_payment_from_date) {
        const filterObj = {
          column: ["invoice_or_payment_date"],
          operator: "GREATER_THAN_EQUAL",
          value: invoice_payment_from_date,
          logicalOperator: "AND",
        };

        const encodedFilter = encodeURIComponent(
          JSON.stringify(filterObj, null, 2),
        );
        finalUrl += `&advanceFilter=${encodedFilter}`;
      }
      if (invoice_payment_to_date) {
        const filterObj = {
          column: ["invoice_or_payment_date"],
          operator: "LESSER_THAN_EQUAL",
          value: invoice_payment_to_date,
          logicalOperator: "AND",
        };

        const encodedFilter = encodeURIComponent(
          JSON.stringify(filterObj, null, 2),
        );
        finalUrl += `&advanceFilter=${encodedFilter}`;
      }

      if (query) {
        finalUrl += "&" + query;
      }

      const res = await api.get(finalUrl);
      const data = res.data.data;
      onCallback(data);
    } catch (err: any) {
      onCallback([]);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const downloadCustomerStatementAsync =
  (
    fromDate: string,
    toDate: string,
    onCallback: () => void,
    customerPolicyId?: number,
    customerId?: number,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      let url = `/accounting/get-customer-statement-pdf`;
      if (customerPolicyId && customerId) {
        url += `?customer_id=${customerId}&customer_policy_id=${customerPolicyId}&from_date=${fromDate}&to_date=${toDate}`;
      } else if (customerId) {
        url += `?customer_id=${customerId}&from_date=${fromDate}&to_date=${toDate}`;
      }
      const res = await api.get(url, {
        responseType: "arraybuffer",
      });

      const pdfBuffer = res.data;

      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = getFilenameFromContentDisposition(res);
      link.click();

      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(fetchCustomerStatementListFailure());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    }
  };

export const upsertCustomerMonthEndStatement =
  (
    type: "invoice" | "payment",
    fromDate: string,
    toDate: string,
    onCallback: (isSuccess: boolean) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    const advanceFilter = [
      {
        column: ["invoice_date"],
        operator: "GREATER_THAN_EQUAL",
        value: fromDate,
        logicalOperator: "AND",
      },
      {
        column: ["invoice_date"],
        operator: "LESSER_THAN_EQUAL",
        value: toDate,
        logicalOperator: "AND",
      },
    ];

    try {
      dispatch(saveLoaderProgress());
      await api.post("/accounting/done-customer-statements", {
        monthend_for: type,
        advanceFilter: advanceFilter,
      });
      const message = "Month end data saved successfully.";
      dispatch(
        showMessage({
          type: "success",
          message: message,
          displayAs: "snackbar",
        }),
      );
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const downloadCustomerStatementPDF =
  (
    customer_id: number,

    from_date: string,
    to_date: string,

    onCallback: () => void,
    customer_policy_id?: number,
    callProducerStatementApi?: boolean,
    callBranchStatementApi?: boolean,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      let url = `/accounting/get-customer-statement-pdf?customer_id=${customer_id}&from_date=${from_date}&to_date=${to_date}`;
      if( callProducerStatementApi){
        url = `/accounting/get-producer-statement-pdf?user_code=${customer_id}&from_date=${from_date}&to_date=${to_date}`;
      }
      if( callBranchStatementApi){
        url = `/accounting/get-branch-statement-pdf?user_code=${customer_id}&from_date=${from_date}&to_date=${to_date}`;
      }
      if (customer_policy_id) {
        url = url + "&customer_policy_id=" + customer_policy_id;
      }
      const res = await api.get(url, {
        responseType: "arraybuffer",
      });

      const pdfBuffer = res.data;

      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = getFilenameFromContentDisposition(res);
      link.click();

      URL.revokeObjectURL(pdfUrl);
      onCallback();
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_CUSTOMER_STATEMENT_STATE = "CLEAR_CUSTOMER_STATEMENT_STATE";
export const clearCustomerStatementState = () =>
  action(CLEAR_CUSTOMER_STATEMENT_STATE);
